import classNames from 'classnames';
import Cookies from 'js-cookie';
import React, { useState } from 'react';
import { Button } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import css from './CookieBanner.module.css';

/**
 * CookieBanner component displays a cookie consent banner with accept and decline options.
 * The banner can be shown/hidden based on user's cookie consent status.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.isVisible controls visibility of the banner
 * @param {Function} props.onAccept callback function when user accepts cookies
 * @param {Function} props.onDecline callback function when user declines cookies
 * @param {string?} props.message custom message to display (optional)
 * @param {string?} props.acceptText custom text for accept button (optional)
 * @param {string?} props.declineText custom text for decline button (optional)
 * @returns {JSX.Element|null} CookieBanner component or null if not visible
 */
const CookieBanner = props => {
  const {
    rootClassName,
    className,
    isVisible = true,
    onAccept = () => {},
    onDecline = () => {},
    message,
    acceptText,
    declineText,
  } = props;

  const [isShown, setIsShown] = useState(isVisible);

  const classes = classNames(rootClassName || css.root, className);

  const handleAccept = () => {
    Cookies.set('cookieConsent', 'true', { expires: 365 });

    if (onAccept) {
      onAccept();
    }
    setIsShown(false);

    window.location.reload();
  };

  const handleDecline = () => {
    Cookies.set('cookieConsent', 'false', { expires: 365 });

    if (onDecline) {
      onDecline();
    }
    setIsShown(false);
  };

  const privacyLink = (
    <span
      className={css.privacyLink}
      onClick={() => {
        window.location.href = '/privacy-policy';
      }}
      role="button"
    >
      <FormattedMessage id="CookieBanner.privacyLinkText" />
    </span>
  );

  if (!isShown) {
    return null;
  }

  return (
    <div className={classes}>
      <div className={css.content}>
        <p className={css.message}>
          {message || <FormattedMessage id="CookieBanner.message" values={{ privacyLink }} />}
        </p>
        <div className={css.buttonContainer}>
          <Button rootClassName={css.acceptButton} onClick={handleAccept}>
            {acceptText || <FormattedMessage id="CookieBanner.accept" />}
          </Button>
          <Button rootClassName={css.declineButton} onClick={handleDecline}>
            {declineText || <FormattedMessage id="CookieBanner.decline" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
